import { CircularProgress } from '@mui/material';
import {
  CompositeFilterDescriptor,
  DataResult,
  DataSourceRequestState,
  toDataSourceRequestString
} from '@progress/kendo-data-query';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { GridSortChangeEvent } from '@progress/kendo-react-grid';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RouteAnimation } from 'src/animations/animations';
import { SecondaryButton } from 'src/components/shared/Button/Button';
import {
  HeaderSection,
  HeaderSectionLeft,
  HeaderSectionRight
} from 'src/components/shared/HeaderSection/HeaderSection';
import { Modal } from 'src/components/shared/Modal/Modal';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { Header1 } from 'src/components/shared/Typography/Header1';
import { useReduxDispatch } from 'src/store';
import { getRequest } from 'src/store/api-handler';
import { isLoadingSpecifications } from 'src/store/requests/requests.selectors';
import { SpecificationDto } from 'src/store/specifications/specification';
import {
  deleteSpecification,
  fetchFilteredSpecifications,
  specificationActions
} from 'src/store/specifications/specifications.actions';
import {
  selectFilteredSpecifications,
  selectSpecificationsRequestState
} from 'src/store/specifications/specifications.selectors';
import { push } from 'src/utils/navigation';
import { SpecificationsList } from './SpecificationsList';
import { isUserAdmin, useAuth } from '../Auth/AuthContext';

export const SpecificationsPage = () => {
  const [specification, setSpecification] =
    useState<Nullable<SpecificationDto>>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showExportSpinner, setShowExportSpinner] = useState<boolean>(false);
  const [exporter, setExporter] = useState<Nullable<ExcelExport>>(null);

  const { t } = useTranslation();
  const { user } = useAuth();
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();

  const fetchSpecifications = useCallback(
    (dataState: DataSourceRequestState) => {
      const queryStr = `${toDataSourceRequestString(dataState)}`;
      dispatch(fetchFilteredSpecifications(queryStr));
    },
    [dispatch]
  );

  const filteredSpecifications = useSelector(selectFilteredSpecifications);
  const requestState = useSelector(selectSpecificationsRequestState);
  const loadingSpecifications = useSelector(isLoadingSpecifications);

  useEffect(() => {
    fetchSpecifications(requestState);
  }, [requestState, fetchSpecifications]);

  const onSortChange = (event: GridSortChangeEvent) => {
    dispatch(
      specificationActions.updateRequestState({
        ...requestState,
        sort: event.sort
      })
    );
  };

  const onFilterChange = (filter: CompositeFilterDescriptor | undefined) => {
    dispatch(
      specificationActions.updateRequestState({
        ...requestState,
        filter: filter,
        skip: 0
      })
    );
  };

  const onResetFilters = useCallback(() => {
    dispatch(
      specificationActions.updateRequestState({
        ...requestState,
        filter: undefined,
        skip: 0
      })
    );
  }, [dispatch, requestState]);

  const onPageChange = (skip: number, take: number) => {
    dispatch(
      specificationActions.updateRequestState({
        ...requestState,
        skip,
        take
      })
    );
  };

  const onCreate = () => {
    push('/specifications/create', navigate, RouteAnimation.SlideRight);
  };

  const onEdit = (specification: SpecificationDto) => {
    push(
      `/specifications/${specification.id}`,
      navigate,
      RouteAnimation.SlideRight
    );
  };

  const onCancel = () => {
    setSpecification(null);
    setShowDeleteModal(false);
  };

  const onDelete = (spec: SpecificationDto) => {
    setSpecification(spec);
    setShowDeleteModal(true);
  };

  const onConfirmDelete = async () => {
    if (specification) {
      await dispatch(deleteSpecification(specification.id, specification));
      setSpecification(null);
      setShowDeleteModal(false);
    }
  };

  const onExport = async () => {
    const newState = { ...requestState, take: undefined, skip: undefined };
    const queryStr = `${toDataSourceRequestString(newState)}`;
    setShowExportSpinner(true);
    const data = await getRequest<DataResult>(
      `/specifications/filtered?${queryStr}`
    );
    exporter!.save(data.data);
    setShowExportSpinner(false);
  };

  return (
    <Fragment>
      <HeaderSection className="mb-32">
        <HeaderSectionLeft>
          <Header1>{t('specification.plural')}</Header1>
          <BodyType>{`${t('crudtext')} ${t('specification.plural')}`}</BodyType>
        </HeaderSectionLeft>
        <HeaderSectionRight>
          {showExportSpinner && (
            <CircularProgress className="mr-32" color="primary" />
          )}
          <SecondaryButton
            onClick={onCreate}
            disabled={!isUserAdmin(user)}
            icon="la-plus"
            className="mr-32"
          >
            {`${t('create.verb')} ${t('specification.singular')}`}
          </SecondaryButton>
          <SecondaryButton onClick={onExport} icon="la-file-excel">
            {`${t('export.excel')}`}
          </SecondaryButton>
        </HeaderSectionRight>
      </HeaderSection>
      <SpecificationsList
        data={filteredSpecifications}
        loading={loadingSpecifications}
        requestState={requestState}
        onEdit={onEdit}
        onDelete={onDelete}
        onSortChange={onSortChange}
        onFilterChange={onFilterChange}
        onResetFilters={onResetFilters}
        onPageChange={onPageChange}
        initExporter={exporter => {
          setExporter(exporter);
        }}
      />
      <Modal
        width={500}
        header={`${t('delete.verb')} ${t('specification.singular')}`}
        confirmText={t('delete.verb')}
        onClose={() => onCancel()}
        onConfirm={() => onConfirmDelete()}
        open={showDeleteModal}
        showActions
      >
        <BodyType>
          {t('specification.delete')} <q>{specification?.name}</q>?
        </BodyType>
      </Modal>
    </Fragment>
  );
};
